import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'Ana Sayfa', path: '/', sectionId: 'ana-sayfa' },
  { label: 'Hakkımızda', path: '/hakkimizda', sectionId: 'hakkimizda' },
  { label: 'Hizmetler', path: '/hizmetler', sectionId: 'hizmetler' },
  { label: 'Hedef Ülkeler', path: '/hedef-ulkeler', sectionId: 'hedef-ulkeler' },
  { label: 'Galeri', path: '/galeri', sectionId: 'galeri' },
  { label: 'İletişim', path: '/iletisim', sectionId: 'iletisim' },
];

const WHATSAPP_NUMBER = '905382323822';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('ana-sayfa');

  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      if (isHome) {
        const sections = ['ana-sayfa', 'hakkimizda', 'hizmetler', 'hedef-ulkeler', 'iletisim'];
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i]);
          if (el && window.scrollY >= el.offsetTop - 100) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  useEffect(() => {
    setMenuOpen(false);
    if (!isHome) window.scrollTo(0, 0);
  }, [location.pathname, isHome]);

  const handleNavClick = (item: typeof navItems[0]) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.getElementById(item.sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(item.path);
      }
    } else {
      navigate(item.path);
    }
  };

  const showWhiteBg = scrolled || !isHome;

  const getIsActive = (item: typeof navItems[0]) => {
    if (isHome) return activeSection === item.sectionId;
    return location.pathname === item.path;
  };

  const iconColor = showWhiteBg ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showWhiteBg ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center gap-3 cursor-pointer flex-shrink-0"
        >
          <img
            src="/assets/images/logo.png"
            alt="Global79 Lojistik Logo"
            className="h-20 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navItems.map((item) => {
            const isActive = getIsActive(item);
            return (
              <li key={item.path}>
                <a
                  href={item.path}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
                  className={`relative text-sm font-semibold tracking-wide uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                    showWhiteBg
                      ? isActive ? 'text-orange-500' : 'text-gray-700 hover:text-orange-500'
                      : isActive ? 'text-orange-400' : 'text-white hover:text-orange-300'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-orange-500 rounded-full" />
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Phone Button */}
          <a
            href="tel:05382323822"
            className="hidden md:flex items-center gap-2 border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white text-sm font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            aria-label="Hemen Ara"
          >
            <i className="ri-phone-fill text-base" />
            0 538 232 38 22
          </a>

          {/* WhatsApp Button */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Merhaba%2C%20lojistik%20hizmetleriniz%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            aria-label="WhatsApp ile İletişim"
          >
            <i className="ri-whatsapp-line text-base" />
            WhatsApp
          </a>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menüyü aç"
          >
            <i className={`text-2xl ${showWhiteBg ? 'text-gray-800' : 'text-white'} ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white shadow-lg px-6 py-4 flex flex-col gap-4">
          {navItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
              className={`font-semibold text-sm uppercase tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                getIsActive(item) ? 'text-orange-500' : 'text-gray-700 hover:text-orange-500'
              }`}
            >
              {item.label}
            </a>
          ))}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Merhaba%2C%20lojistik%20hizmetleriniz%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-sm px-4 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap justify-center"
          >
            <i className="ri-whatsapp-line text-base" />
            WhatsApp ile Ulaş
          </a>
        </div>
      )}
    </nav>
  );
}
